/**
 * Vật tư 2 đơn vị tính (04/11/2026): ĐVT chính = tồn kho / xuất SX / giá vốn; ĐVT phụ (không bắt buộc) = mua / đóng gói.
 * Quy ước hệ số: 1 ĐVT phụ = secondaryFactor × ĐVT chính (VD 1 Ram = 500 Tờ, 1 Tấn = 1000 Kg).
 * Chứng từ nhập theo ĐVT phụ lưu cả số gõ (entry_*) và số quy đổi ra ĐVT chính — máy chủ tự tính bằng các hàm dưới đây.
 */
export interface UnitConversionMaterial {
  unit?: string | null;
  secondaryUnit?: string | null;
  secondaryFactor?: number | string | { toString(): string } | null;
}

export interface PaperConversionInput {
  unit?: string | null;
  secondaryUnit?: string | null;
  gsm?: number | string | null;
  width?: number | string | null;
  length?: number | string | null;
  sheetsPerReam?: number | null;
}

const toNum = (v: unknown): number => {
  if (v === null || v === undefined || v === '') return NaN;
  return typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
};

const roundTo = (v: number, decimals: number) => {
  const f = 10 ** decimals;
  return Math.round(v * f) / f;
};

const key = (u?: string | null) => String(u ?? '').trim().toLowerCase();

/** Hệ số đã khai và hợp lệ (> 0), không thì null */
export function secondaryFactorOf(m: UnitConversionMaterial | null | undefined): number | null {
  if (!m?.secondaryUnit) return null;
  const f = toNum(m.secondaryFactor);
  return f > 0 ? f : null;
}

/** Số tờ trong 1 kg giấy = 10⁹ / (GSM × rộng mm × dài mm); null nếu thiếu số */
export function paperSheetsPerKg(gsm: unknown, widthMm: unknown, lengthMm: unknown): number | null {
  const g = toNum(gsm);
  const w = toNum(widthMm);
  const l = toNum(lengthMm);
  if (!(g > 0) || !(w > 0) || !(l > 0)) return null;
  return 1e9 / (g * w * l);
}

/**
 * Gợi ý hệ số "1 ĐVT phụ = N ĐVT chính" cho giấy / ĐVT khối lượng; null nếu không suy ra được.
 * Giấy cần GSM + khổ (mm) khi quy đổi giữa Tờ / Ram và Kg / Tấn.
 */
export function suggestSecondaryFactor(m: PaperConversionInput): number | null {
  const p = key(m.unit);
  const s = key(m.secondaryUnit);
  if (!p || !s || p === s) return null;
  const ream = m.sheetsPerReam && m.sheetsPerReam > 0 ? m.sheetsPerReam : 500;
  // Số Tờ tương ứng 1 đơn vị (Tờ = 1, Ram = ream) và số Kg tương ứng 1 đơn vị (Kg = 1, Tấn = 1000)
  const sheetUnits: Record<string, number> = { 'tờ': 1, ram: ream };
  const massUnits: Record<string, number> = { kg: 1, 'tấn': 1000 };
  if (p in massUnits && s in massUnits) return massUnits[s] / massUnits[p];
  if (p in sheetUnits && s in sheetUnits) return sheetUnits[s] / sheetUnits[p];
  const perKg = paperSheetsPerKg(m.gsm, m.width, m.length);
  if (!perKg) return null;
  // chính Tờ / Ram, phụ Kg / Tấn: 1 phụ = (kg của phụ × tờ/kg) / tờ của chính
  if (p in sheetUnits && s in massUnits) return roundTo((massUnits[s] * perKg) / sheetUnits[p], 6);
  // chính Kg / Tấn, phụ Tờ / Ram: 1 phụ = (tờ của phụ / tờ/kg) / kg của chính
  if (p in massUnits && s in sheetUnits) return roundTo(sheetUnits[s] / perKg / massUnits[p], 6);
  return null;
}

/**
 * Quy đổi số gõ theo entryUnit ra ĐVT chính. entryUnit rỗng / trùng ĐVT chính → giữ nguyên.
 * Trả null khi entryUnit không phải ĐVT chính / phụ của vật tư hoặc thiếu hệ số (máy chủ phải từ chối).
 */
export function toPrimaryQty(
  entryQty: number,
  entryUnit: string | null | undefined,
  m: UnitConversionMaterial,
  decimals = 3,
): number | null {
  if (!Number.isFinite(entryQty)) return null;
  if (!entryUnit || key(entryUnit) === key(m.unit)) return entryQty;
  if (key(entryUnit) !== key(m.secondaryUnit)) return null;
  const f = secondaryFactorOf(m);
  return f ? roundTo(entryQty * f, decimals) : null;
}

/** Đơn giá theo entryUnit → đơn giá theo ĐVT chính (chia hệ số); null khi không quy đổi được */
export function toPrimaryUnitPrice(
  entryUnitPrice: number,
  entryUnit: string | null | undefined,
  m: UnitConversionMaterial,
): number | null {
  if (!Number.isFinite(entryUnitPrice)) return null;
  if (!entryUnit || key(entryUnit) === key(m.unit)) return entryUnitPrice;
  if (key(entryUnit) !== key(m.secondaryUnit)) return null;
  const f = secondaryFactorOf(m);
  return f ? roundTo(entryUnitPrice / f, 2) : null;
}

/** Số lượng ĐVT chính → ĐVT phụ; null nếu vật tư không có ĐVT phụ */
export function fromPrimaryQty(qty: number, m: UnitConversionMaterial, decimals = 3): number | null {
  const f = secondaryFactorOf(m);
  if (!f || !Number.isFinite(qty)) return null;
  return roundTo(qty / f, decimals);
}

/** Định dạng số kiểu Việt Nam (1.234,5) tối đa `decimals` chữ số lẻ */
export function formatUnitQty(qty: number, decimals = 3): string {
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: decimals }).format(roundTo(qty, decimals));
}

/** "5.000 Tờ (10 Ram)" — phần ngoặc chỉ hiện khi vật tư có ĐVT phụ */
export function formatDualQty(
  qty: number,
  m: UnitConversionMaterial,
  opts: { primaryDecimals?: number; secondaryDecimals?: number } = {},
): string {
  const main = `${formatUnitQty(qty, opts.primaryDecimals ?? 3)}${m.unit ? ` ${m.unit}` : ''}`;
  const sec = fromPrimaryQty(qty, m, opts.secondaryDecimals ?? 3);
  return sec === null ? main : `${main} (${formatUnitQty(sec, opts.secondaryDecimals ?? 3)} ${m.secondaryUnit})`;
}

/** "1 Ram = 500 Tờ"; rỗng nếu chưa khai ĐVT phụ */
export function packingLabel(m: UnitConversionMaterial): string {
  const f = secondaryFactorOf(m);
  if (!f || !m.unit) return '';
  return `1 ${m.secondaryUnit} = ${formatUnitQty(f, 6)} ${m.unit}`;
}
